import moment from 'moment';
import * as types from '../types/orders';

const initialState = {
    liveOrders: [],
    pastOrders: [],
    failedOrders: [],
    abandonedCarts: [],
    liveOrderState: {},
    currentOrderState: 'all',
    currentOrderingMode: 'all',
    ordersCount: {
        all: 0,
        placed: 0,
        restaurant_accepted: 0,
        restaurant_ready: 0,
        rider_picked_up: 0,
    },
    selectedOrderId: 0,
    tableLoader: { loader: false, error: false },
    newOrders: 0,
    selectedOrderDetails: {},
    orderDetailsLoader: { loader: true, error: false },
    date: { startDate: moment(), endDate: moment() },
    orderCurrentPage: { current_page: 0, page_size: 10 },
    hasRestaurantAcceptedOrder: false,
    liveOrdersPagination: {
        currentPage: 1,
        totalPages: 0,
        totalCount: 0,
        perPage: 10,
    },
    _lastApiSyncAt: {},
};

export default (state = initialState, action) => {
    switch (action.type) {
        case types.SET_LIVE_ORDERS:
            // API response is the source of truth — use it directly
            // Only keep notification-appended orders that are freshly "placed"
            // (new orders the API hasn't picked up yet). Once an order progresses
            // past "placed", it must appear in the API response — if it doesn't, it's stale.
            const apiOrderIds = action.payload.map(order => order.order_id);

            const freshNotificationOrders = state.liveOrders.filter(order =>
                order._fromNotification &&
                !apiOrderIds.includes(order.order_id) &&
                order.delivery_status === 'placed'
            );

            const mergedOrders = [...freshNotificationOrders, ...action.payload];
            
            return {
                ...state,
                liveOrders: mergedOrders,
                _lastApiSyncAt: {},
            };
        case types.SET_PAST_ORDERS:
            return {
                ...state,
                pastOrders: action.payload,
            };

        case types.SET_FAILED_ORDERS:
            return {
                ...state,
                failedOrders: action.payload,
            };

        case types.SET_ABANDONED_CARTS:
            return {
                ...state,
                abandonedCarts: action.payload,
            };
        case types.SET_LIVE_ORDER_STATE:
            return {
                ...state,
                liveOrderState: action.payload,
            };
        case types.SET_CURRENT_ORDER_STATE:
            return {
                ...state,
                currentOrderState: action.payload,
            };
        case types.SET_CURRENT_ORDER_MODE:
            return {
                ...state,
                currentOrderingMode: action.payload,
            };
        case types.SET_ORDERS_COUNT:
            return {
                ...state,
                ordersCount: action.payload,
            };
        case types.SET_SELECTED_ORDER:
            return {
                ...state,
                selectedOrderId: action.payload,
            };
        case types.SET_TABLE_LOADER:
            return {
                ...state,
                tableLoader: action.payload,
            };
        case types.SET_NEW_ORDERS:
            return {
                ...state,
                newOrders: action.payload,
            };
        case types.SET_SELECTED_ORDER_DETAILS:
            return {
                ...state,
                selectedOrderDetails: action.payload,
            };
        case types.SET_ORDER_DETAILS_LOADER:
            return {
                ...state,
                orderDetailsLoader: action.payload,
            };
        case types.SET_ORDER_FILTER_DATE:
            return {
                ...state,
                date: action.payload,
            };
        case types.SET_ORDER_CURRENT_PAGE:
            return {
                ...state,
                orderCurrentPage: action.payload,
            };
        case types.HAS_RESTAURANT_ACCEPTED_ORDER:
            return {
                ...state,
                hasRestaurantAcceptedOrder: action.payload,
            };
        case types.SET_LIVE_ORDERS_PAGINATION:
            return {
                ...state,
                liveOrdersPagination: action.payload,
            };
        case types.APPEND_NEW_ORDER_FROM_NOTIFICATION:
            // Check if order already exists
            const existingOrderIndex = state.liveOrders.findIndex(
                (order) => order.order_id === action.payload.order_id
            );

            // If order already exists, don't add it again
            if (existingOrderIndex !== -1) {
                return state;
            }

            // Always append new orders from notifications
            // Mark it as from notification for proper handling during pagination
            const orderWithNotificationFlag = {
                ...action.payload,
                _fromNotification: true  // Internal flag to track notification-appended orders
            };
            
            // Prepend new order to the beginning of the list (newest first)
            const newLiveOrders = [orderWithNotificationFlag, ...state.liveOrders];

            // If this is the first order, select it automatically
            const selectedOrderId = state.liveOrders.length === 0 ? action.payload.order_id : state.selectedOrderId;

            return {
                ...state,
                liveOrders: newLiveOrders,
                selectedOrderId: selectedOrderId,
                ordersCount: {
                    ...state.ordersCount,
                    all: state.ordersCount.all + 1,
                    placed: state.ordersCount.placed + 1,
                },
                liveOrdersPagination: {
                    ...state.liveOrdersPagination,
                    totalCount: state.liveOrdersPagination.totalCount + 1,
                },
            };
        case types.SYNC_ORDER_STATUS_IN_LIST: {
            const { orderId, orderData } = action.payload;
            const syncOrderIndex = state.liveOrders.findIndex(o => o.order_id === orderId);

            if (syncOrderIndex === -1) return state;

            // Flatten nested status fields so the list table can read them
            // List items use flat fields (e.g., liveOrder.restaurant_ready_at)
            // but PATCH response has nested fields (e.g., order.status.restaurant_ready_at)
            const flatStatusFields = orderData.status ? {
                delivery_status: orderData.status.delivery_status,
                placed_at: orderData.status.placed_at,
                restaurant_accepted_at: orderData.status.restaurant_accepted_at,
                restaurant_ready_at: orderData.status.restaurant_ready_at,
                rider_picked_up_at: orderData.status.rider_picked_up_at,
                rider_delivered_at: orderData.status.rider_delivered_at,
                customer_picked_up_at: orderData.status.customer_picked_up_at,
                restaurant_cancelled_at: orderData.status.restaurant_cancelled_at,
                delivered_at: orderData.status.delivered_at,
                rider_reached_restaurant_at: orderData.status.rider_reached_restaurant_at,
                rider_accept_at: orderData.status.rider_accept_at,
                rider_reject_at: orderData.status.rider_reject_at,
            } : {};

            // Update only the changed order in the list (targeted, not full replace)
            const syncUpdatedOrders = [...state.liveOrders];
            syncUpdatedOrders[syncOrderIndex] = {
                ...syncUpdatedOrders[syncOrderIndex],
                ...orderData,
                ...flatStatusFields,
                delivery_status: orderData.delivery_status || orderData.status?.delivery_status,
            };

            // Update selectedOrderDetails if this is the selected order
            let syncUpdatedDetails = state.selectedOrderDetails;
            if (state.selectedOrderId === orderId && state.selectedOrderDetails?.order_id === orderId) {
                syncUpdatedDetails = {
                    ...state.selectedOrderDetails,
                    ...orderData,
                    status: {
                        ...state.selectedOrderDetails.status,
                        ...(orderData.status || {}),
                    },
                };
            }

            // Recalculate counts based on status transition
            const syncOldStatus = state.liveOrders[syncOrderIndex].delivery_status;
            const syncNewStatus = orderData.delivery_status || orderData.status?.delivery_status;
            let syncCounts = { ...state.ordersCount };
            if (syncOldStatus !== syncNewStatus && syncNewStatus) {
                if (syncOldStatus && syncCounts[syncOldStatus] > 0) syncCounts[syncOldStatus]--;
                syncCounts[syncNewStatus] = (syncCounts[syncNewStatus] || 0) + 1;
            }

            return {
                ...state,
                liveOrders: syncUpdatedOrders,
                selectedOrderDetails: syncUpdatedDetails,
                ordersCount: syncCounts,
                _lastApiSyncAt: {
                    ...state._lastApiSyncAt,
                    [orderId]: Date.now(),
                },
            };
        }
        case types.UPDATE_ORDER_FROM_NOTIFICATION: {
            // DEDUP: Skip if API already synced this order within last 3 seconds
            const lastSync = state._lastApiSyncAt?.[action.payload.order_id];
            if (lastSync && (Date.now() - lastSync) < 3000) {
                return state;
            }

            // Find the order to update
            const orderToUpdateIndex = state.liveOrders.findIndex(
                (order) => order.order_id === action.payload.order_id
            );

            // If order doesn't exist in the list, don't update
            if (orderToUpdateIndex === -1) {
                return state;
            }

            // Create a new array with the updated order
            // Preserve the _fromNotification flag if it exists
            const updatedLiveOrders = [...state.liveOrders];
            updatedLiveOrders[orderToUpdateIndex] = {
                ...action.payload,
                _fromNotification: state.liveOrders[orderToUpdateIndex]._fromNotification
            };

            // Update the selected order details if it's the currently selected order
            let updatedSelectedOrderDetails = state.selectedOrderDetails;
            if (state.selectedOrderId === action.payload.order_id) {
                // If we have detailed order info in selectedOrderDetails, merge the updates
                if (state.selectedOrderDetails && state.selectedOrderDetails.order_id === action.payload.order_id) {
                    // Merge the notification payload with existing details to preserve full order info
                    updatedSelectedOrderDetails = {
                        ...state.selectedOrderDetails,
                        ...action.payload,
                        // Update the status object with notification data
                        status: {
                            ...state.selectedOrderDetails.status,
                            delivery_status: action.payload.delivery_status,
                            placed_at: action.payload.placed_at,
                            restaurant_accepted_at: action.payload.restaurant_accepted_at,
                            restaurant_ready_at: action.payload.restaurant_ready_at,
                            restaurant_cancelled_at: action.payload.restaurant_cancelled_at,
                            rider_picked_up_at: action.payload.rider_picked_up_at,
                            customer_picked_up_at: action.payload.customer_picked_up_at,
                            delivered_at: action.payload.delivered_at,
                        },
                        // Preserve nested objects that might not be in notification
                        cart_items: state.selectedOrderDetails.cart_items || action.payload.cart_items,
                        restaurant: state.selectedOrderDetails.restaurant || action.payload.restaurant,
                        outlet: state.selectedOrderDetails.outlet || action.payload.outlet,
                    };
                } else {
                    // If no existing details, create proper status structure
                    updatedSelectedOrderDetails = {
                        ...action.payload,
                        status: {
                            delivery_status: action.payload.delivery_status,
                            placed_at: action.payload.placed_at,
                            restaurant_accepted_at: action.payload.restaurant_accepted_at,
                            restaurant_ready_at: action.payload.restaurant_ready_at,
                            restaurant_cancelled_at: action.payload.restaurant_cancelled_at,
                            rider_picked_up_at: action.payload.rider_picked_up_at,
                            customer_picked_up_at: action.payload.customer_picked_up_at,
                            delivered_at: action.payload.delivered_at,
                        },
                    };
                }
            }

            // Calculate updated order counts
            const oldStatus = state.liveOrders[orderToUpdateIndex].delivery_status;
            const newStatus = action.payload.delivery_status;
            let updatedOrdersCount = { ...state.ordersCount };

            // Decrement old status count
            if (oldStatus === 'placed' && updatedOrdersCount.placed > 0) {
                updatedOrdersCount.placed--;
            } else if (oldStatus === 'restaurant_accepted' && updatedOrdersCount.restaurant_accepted > 0) {
                updatedOrdersCount.restaurant_accepted--;
            } else if (oldStatus === 'restaurant_ready' && updatedOrdersCount.restaurant_ready > 0) {
                updatedOrdersCount.restaurant_ready--;
            } else if (oldStatus === 'rider_picked_up' && updatedOrdersCount.rider_picked_up > 0) {
                updatedOrdersCount.rider_picked_up--;
            }

            // Increment new status count
            if (newStatus === 'placed') {
                updatedOrdersCount.placed++;
            } else if (newStatus === 'restaurant_accepted') {
                updatedOrdersCount.restaurant_accepted++;
            } else if (newStatus === 'restaurant_ready') {
                updatedOrdersCount.restaurant_ready++;
            } else if (newStatus === 'rider_picked_up') {
                updatedOrdersCount.rider_picked_up++;
            }

            return {
                ...state,
                liveOrders: updatedLiveOrders,
                selectedOrderDetails: updatedSelectedOrderDetails,
                ordersCount: updatedOrdersCount,
            };
        }
        case types.REMOVE_COMPLETED_ORDER:
            // Find the order to remove
            const orderToRemoveIndex = state.liveOrders.findIndex((order) => order.order_id === action.payload);

            // If order doesn't exist, don't do anything
            if (orderToRemoveIndex === -1) {
                return state;
            }

            // Get the order details before removing
            const orderToRemove = state.liveOrders[orderToRemoveIndex];

            // Remove the order from the array
            const filteredLiveOrders = state.liveOrders.filter((order) => order.order_id !== action.payload);

            // Update order counts
            let newOrdersCount = { ...state.ordersCount };

            // Decrement the total count
            if (newOrdersCount.all > 0) {
                newOrdersCount.all--;
            }

            // Decrement the specific status count
            const statusToDecrement = orderToRemove.delivery_status;
            if (statusToDecrement === 'placed' && newOrdersCount.placed > 0) {
                newOrdersCount.placed--;
            } else if (statusToDecrement === 'restaurant_accepted' && newOrdersCount.restaurant_accepted > 0) {
                newOrdersCount.restaurant_accepted--;
            } else if (statusToDecrement === 'restaurant_ready' && newOrdersCount.restaurant_ready > 0) {
                newOrdersCount.restaurant_ready--;
            } else if (statusToDecrement === 'rider_picked_up' && newOrdersCount.rider_picked_up > 0) {
                newOrdersCount.rider_picked_up--;
            }

            // Handle selected order logic
            let newSelectedOrderId = state.selectedOrderId;
            let newSelectedOrderDetails = state.selectedOrderDetails;

            // If the removed order was selected
            if (state.selectedOrderId === action.payload) {
                // Select the next available order
                if (filteredLiveOrders.length > 0) {
                    // Try to select the order at the same position, or the last one if out of bounds
                    const newSelectedIndex = Math.min(orderToRemoveIndex, filteredLiveOrders.length - 1);
                    newSelectedOrderId = filteredLiveOrders[newSelectedIndex].order_id;
                    newSelectedOrderDetails = {}; // Will be fetched when needed
                } else {
                    // No orders left
                    newSelectedOrderId = '';
                    newSelectedOrderDetails = {};
                }
            }

            return {
                ...state,
                liveOrders: filteredLiveOrders,
                selectedOrderId: newSelectedOrderId,
                selectedOrderDetails: newSelectedOrderDetails,
                ordersCount: newOrdersCount,
                liveOrdersPagination: {
                    ...state.liveOrdersPagination,
                    totalCount: Math.max(0, state.liveOrdersPagination.totalCount - 1),
                },
            };
        default:
            return state;
    }
};
